/**
 * Earn program: volume tiers with fee cashback, points (trading + quests + referrals) and a
 * three-level referral program paid as a share of the trading fees referred users pay.
 * Everything is computed from live-account fills; demo trading never earns.
 */
import { DEFAULT_MISSION_SETTINGS, validateMissionSettings } from './missions.js';
export const REWARD_TIERS = [
    { id: 'wood', multiplier: 1, name: 'Wood', minVolume: 0, cashbackPct: 5, color: '#a47551', reachPoints: 0 },
    { id: 'bronze', multiplier: 1.25, name: 'Bronze', minVolume: 50_000, cashbackPct: 10, color: '#cd7f32', reachPoints: 1000 },
    { id: 'silver', multiplier: 1.5, name: 'Silver', minVolume: 250_000, cashbackPct: 12.5, color: '#b8c0cc', reachPoints: 2000 },
    { id: 'gold', multiplier: 1.75, name: 'Gold', minVolume: 1_000_000, cashbackPct: 15, color: '#f5c542', reachPoints: 5000 },
    { id: 'platinum', multiplier: 2, name: 'Platinum', minVolume: 5_000_000, cashbackPct: 17.5, color: '#7fd6d0', reachPoints: 10_000 },
    { id: 'diamond', multiplier: 2.5, name: 'Diamond', minVolume: 25_000_000, cashbackPct: 20, color: '#8fb4ff', reachPoints: 15_000 },
    { id: 'champion', multiplier: 3, name: 'Champion', minVolume: 100_000_000, cashbackPct: 25, color: '#c58bff', reachPoints: 25_000 },
];
/** Tier for a cumulative volume, plus the next one (null at the top). */
export function tierFor(volume, tiers = REWARD_TIERS) {
    let i = 0;
    while (i + 1 < tiers.length && volume >= tiers[i + 1].minVolume)
        i++;
    return { tier: tiers[i], next: tiers[i + 1] ?? null };
}
/** Share of the trading fees paid by users at each referral level, in percent (level 1 = people you invited). */
export const REFERRAL_LEVELS = [
    { level: 1, pct: 30 },
    { level: 2, pct: 3 },
    { level: 3, pct: 2 },
];
/** One-time reward for each level-1 friend once they are verified and have made a live trade. */
export const REFERRAL_BONUS_USD = 250;
/** Trading points: one point per this much USD of live volume (before the tier multiplier). */
export const POINTS_PER_USD = 100;
/** Referral points: this percent of the trading points earned by your level-1 referrals. */
export const REFERRAL_POINTS_PCT = 10;
export const QUESTS = [
    { id: 'verify', title: 'Verify your identity', text: 'Complete KYC verification.', points: 500, to: '/account/kyc', cta: 'Verify', starter: true },
    { id: 'two_fa', title: 'Turn on 2FA', text: 'Protect your account with an authenticator app.', points: 250, to: '/account/security', cta: 'Set up', starter: true },
    { id: 'deposit', title: 'Make your first deposit', text: 'Fund your live account.', points: 1000, to: '/account/deposit', cta: 'Deposit', starter: true },
    // Retired: usernames are generated at sign-up and can't be changed (migration 036).
    { id: 'username', title: 'Pick your username', text: 'Choose the name you appear under on the leaderboard.', points: 100, to: '/earn/leaderboard', cta: 'Pick a name', retired: true },
    { id: 'first_trade', title: 'Place your first live trade', text: 'Open any position on your live account.', points: 250, to: '/trade', cta: 'Trade', starter: true },
    { id: 'sl_trade', title: 'Protect a trade with a stop loss', text: 'Open a live position with a stop loss, so a bad move can only cost what you planned.', points: 300, to: '/trade', cta: 'Trade', starter: true },
    { id: 'refer_1', title: 'Invite a friend', text: 'Get one friend to sign up with your link.', points: 500, to: '/earn/referrals', cta: 'Invite', target: 1, starter: true, ladder: 'friends' },
    { id: 'volume_10k', title: 'Trade $10K volume', text: 'Reach $10,000 in live trading volume.', points: 1000, to: '/trade', cta: 'Trade', target: 10_000, ladder: 'volume' },
    { id: 'volume_100k', title: 'Trade $100K volume', text: 'Reach $100,000 in live trading volume.', points: 5000, to: '/trade', cta: 'Trade', target: 100_000, ladder: 'volume' },
    { id: 'volume_1m', title: 'Trade $1M volume', text: 'Reach $1,000,000 in live trading volume.', points: 15_000, to: '/trade', cta: 'Trade', target: 1_000_000, ladder: 'volume' },
    { id: 'volume_10m', title: 'Trade $10M volume', text: 'Reach $10,000,000 in live trading volume.', points: 50_000, to: '/trade', cta: 'Trade', target: 10_000_000, ladder: 'volume' },
    { id: 'refer_5', title: 'Invite 5 friends', text: 'Get five friends to sign up with your link.', points: 3000, to: '/earn/referrals', cta: 'Invite', target: 5, ladder: 'friends' },
    { id: 'days_10', title: 'Trade on 10 days', text: 'Make live trades on 10 different days.', points: 500, to: '/trade', cta: 'Trade', target: 10, ladder: 'days' },
    { id: 'days_50', title: 'Trade on 50 days', text: 'Make live trades on 50 different days.', points: 2000, to: '/trade', cta: 'Trade', target: 50, ladder: 'days' },
    { id: 'days_100', title: 'Trade on 100 days', text: 'Make live trades on 100 different days.', points: 5000, to: '/trade', cta: 'Trade', target: 100, ladder: 'days' },
    { id: 'days_250', title: 'Trade on 250 days', text: 'Make live trades on 250 different days.', points: 12_000, to: '/trade', cta: 'Trade', target: 250, ladder: 'days' },
    { id: 'streak_4', title: '4-week streak', text: 'Keep your weekly mission streak for 4 weeks in a row.', points: 1000, to: '/earn/missions', cta: 'Missions', target: 4, ladder: 'streak' },
    { id: 'streak_12', title: '12-week streak', text: 'Keep your weekly mission streak for 12 weeks in a row.', points: 3000, to: '/earn/missions', cta: 'Missions', target: 12, ladder: 'streak' },
    { id: 'streak_26', title: '26-week streak', text: 'Keep your weekly mission streak for 26 weeks in a row.', points: 8000, to: '/earn/missions', cta: 'Missions', target: 26, ladder: 'streak' },
    { id: 'streak_52', title: '52-week streak', text: 'Keep your weekly mission streak for a whole year.', points: 20_000, to: '/earn/missions', cta: 'Missions', target: 52, ladder: 'streak' },
    // Academy: finish a track (every lesson's quiz passed). Points only, never cash.
    { id: 'academy_start', title: 'Finish "Markets 101"', text: 'Complete the first Academy track.', points: 250, to: '/academy/how-markets-work', cta: 'Learn' },
    { id: 'academy_leverage', title: 'Learn leverage and margin', text: 'Complete the Leverage and margin track in the Academy.', points: 500, to: '/academy/leverage-margin', cta: 'Learn' },
    { id: 'academy_risk', title: 'Learn risk management', text: 'Complete the Risk management track in the Academy.', points: 750, to: '/academy/risk-management', cta: 'Learn' },
    { id: 'academy_beginner', title: 'Finish every beginner track', text: 'Complete all beginner tracks in the Academy.', points: 1500, to: '/academy/tracks', cta: 'Learn' },
];
/** Why a friend's reward was held for a check: what they share with the person who invited them. */
export const REFERRAL_FLAGS = ['same_ip', 'same_name', 'same_phone', 'same_payout'];
export const REFERRAL_FLAG_LABEL = {
    same_ip: 'Same IP address',
    same_name: 'Same name',
    same_phone: 'Same phone number',
    same_payout: 'Same payout account',
};
export const LEADERBOARD_SAMPLES = ['off', 'preview'];
export const DEFAULT_EARN_SETTINGS = {
    tiers: REWARD_TIERS.map((t) => ({ ...t })),
    pointsPerUsd: POINTS_PER_USD,
    questPoints: Object.fromEntries(QUESTS.map((q) => [q.id, q.points])),
    referral: {
        bonusUsd: REFERRAL_BONUS_USD,
        levelPcts: [REFERRAL_LEVELS[0].pct, REFERRAL_LEVELS[1].pct, REFERRAL_LEVELS[2].pct],
        pointsPct: REFERRAL_POINTS_PCT,
        applyWindowDays: 7,
        milestones: [
            { friends: 10, usd: 500 },
            { friends: 25, usd: 1500 },
        ],
    },
    leaderboard: { samples: 'preview' },
    missions: DEFAULT_MISSION_SETTINGS,
};
/** Returns an error message, or null when the settings are valid. */
export function validateEarnSettings(s) {
    if (!Array.isArray(s.tiers) || s.tiers.length < 1 || s.tiers.length > 12)
        return 'Keep between 1 and 12 tiers';
    if (s.tiers[0].minVolume !== 0)
        return 'The first tier must start at $0 volume';
    for (let i = 0; i < s.tiers.length; i++) {
        const t = s.tiers[i];
        if (!t.id || !/^[a-z0-9_]{1,20}$/.test(t.id))
            return `Tier ${i + 1}: invalid id`;
        if (!t.name?.trim() || t.name.length > 24)
            return `Tier ${i + 1}: enter a name (up to 24 characters)`;
        if (!(t.cashbackPct >= 0 && t.cashbackPct <= 100))
            return `${t.name}: cashback must be 0–100%`;
        if (!(t.multiplier >= 0 && t.multiplier <= 100))
            return `${t.name}: multiplier must be 0–100`;
        if (!/^#[0-9a-f]{6}$/i.test(t.color))
            return `${t.name}: colour must be a hex value like #a47551`;
        if (!(Number.isInteger(t.reachPoints) && t.reachPoints >= 0 && t.reachPoints <= 10_000_000))
            return `${t.name}: reach points must be a whole number, 0 or more`;
        if (i > 0 && !(t.minVolume > s.tiers[i - 1].minVolume))
            return `${t.name}: volume must be higher than ${s.tiers[i - 1].name}`;
    }
    if (new Set(s.tiers.map((t) => t.id)).size !== s.tiers.length)
        return 'Tier ids must be unique';
    if (!(s.pointsPerUsd > 0))
        return 'Points per USD must be above 0';
    for (const q of QUESTS)
        if (!(s.questPoints?.[q.id] >= 0))
            return `${q.title}: points must be 0 or more`;
    const r = s.referral;
    if (!(r.bonusUsd >= 0 && r.bonusUsd <= 100_000))
        return 'Referral bonus must be $0–$100,000';
    if (r.levelPcts.length !== 3 || r.levelPcts.some((p) => !(p >= 0 && p <= 100)))
        return 'Level percentages must be 0–100%';
    if (r.levelPcts[0] + r.levelPcts[1] + r.levelPcts[2] > 100)
        return 'The three levels together can pay at most 100% of fees';
    if (!(r.pointsPct >= 0 && r.pointsPct <= 100))
        return 'Referral points must be 0–100%';
    if (!(Number.isInteger(r.applyWindowDays) && r.applyWindowDays >= 0 && r.applyWindowDays <= 365))
        return 'Code window must be 0–365 days';
    if (!Array.isArray(r.milestones) || r.milestones.length > 10)
        return 'Keep up to 10 friend milestones';
    for (let i = 0; i < r.milestones.length; i++) {
        const m = r.milestones[i];
        if (!(Number.isInteger(m.friends) && m.friends >= 2 && m.friends <= 10_000))
            return 'Milestone friends must be a whole number from 2';
        if (!(m.usd >= 0 && m.usd <= 100_000))
            return `Milestone at ${m.friends} friends: amount must be $0–$100,000`;
        if (i > 0 && !(m.friends > r.milestones[i - 1].friends))
            return 'List milestones from fewest friends to most, without repeats';
    }
    if (!LEADERBOARD_SAMPLES.includes(s.leaderboard?.samples))
        return 'Choose who sees the sample leaderboard';
    return validateMissionSettings(s.missions);
}
export const REFERRAL_CODE_RE = /^[A-Za-z0-9_]{3,16}$/;
/** Public username shown on the leaderboard and in referral lists. */
export const USERNAME_RE = /^[A-Za-z0-9_]{3,20}$/;
//# sourceMappingURL=rewards.js.map